/* Apple-style smooth-scroll — a custom `requestAnimationFrame` loop
 * with the same `cubic-bezier(0.16, 1, 0.3, 1)` easing curve we use for
 * framer-motion entries. Native `scrollIntoView({behavior:"smooth"})`
 * is fine on macOS but stutters on Windows + non-Chromium browsers,
 * and clashes with concurrent React re-renders. This helper:
 *
 *   • locks to the rAF clock so the animation never fights React
 *   • runs even while reactive state is updating mid-scroll
 *   • cancels itself when the user wheel-scrolls (Apple-correct)
 *   • supports both pixel target and Element target
 *   • respects `prefers-reduced-motion`
 */

const REDUCED = typeof window !== "undefined" && window.matchMedia
  ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
  : false;

// Apple's "iOS spring-out" easing curve — same we use everywhere.
function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

interface ScrollOptions {
  durationMs?: number;
  offset?: number;     // px subtracted from target.y (e.g. fixed header height)
  onDone?: () => void;
}

let activeAnimation: number | null = null;

function cancelActive(): void {
  if (activeAnimation != null) {
    cancelAnimationFrame(activeAnimation);
    activeAnimation = null;
  }
}

export function smoothScrollTo(targetY: number, opts: ScrollOptions = {}): void {
  if (typeof window === "undefined") return;
  const offset = opts.offset ?? 16;
  const finalY = Math.max(0, targetY - offset);
  if (REDUCED) {
    window.scrollTo({ top: finalY, behavior: "auto" });
    opts.onDone?.();
    return;
  }
  cancelActive();
  const startY = window.scrollY;
  const dy = finalY - startY;
  if (Math.abs(dy) < 2) { opts.onDone?.(); return; }
  // Apple-correct: longer scrolls take more time (logarithmic).
  const baseMs = opts.durationMs ?? Math.min(900, 380 + Math.abs(dy) * 0.32);
  const t0 = performance.now();

  // Cancel on user wheel/touch — matches Apple's "interruptible scroll".
  function onUserScroll(): void { cancelActive(); cleanup(); }
  window.addEventListener("wheel",     onUserScroll, { passive: true, once: true });
  window.addEventListener("touchstart", onUserScroll, { passive: true, once: true });
  function cleanup(): void {
    window.removeEventListener("wheel",     onUserScroll);
    window.removeEventListener("touchstart", onUserScroll);
  }

  function step(now: number): void {
    const elapsed = now - t0;
    const k = Math.min(1, elapsed / baseMs);
    const eased = easeOutExpo(k);
    window.scrollTo(0, Math.round(startY + dy * eased));
    if (k < 1) {
      activeAnimation = requestAnimationFrame(step);
    } else {
      activeAnimation = null;
      cleanup();
      opts.onDone?.();
    }
  }
  activeAnimation = requestAnimationFrame(step);
}

export function smoothScrollToElement(el: Element | null, opts: ScrollOptions = {}): void {
  if (!el) return;
  const rect = (el as HTMLElement).getBoundingClientRect();
  const targetY = (window.scrollY + rect.top);
  smoothScrollTo(targetY, opts);
}

/** Returns true if the selector matched and a scroll was kicked off. */
export function smoothScrollToSelector(selector: string, opts: ScrollOptions = {}): boolean {
  if (typeof document === "undefined") return false;
  const el = document.querySelector(selector);
  if (!el) return false;
  smoothScrollToElement(el, opts);
  return true;
}
